import { useCallback, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    useArchiveChat,
    useDeleteChat,
    useMoveChat,
    usePinChat,
    useUnpinChat,
} from "~/entities/chat/chat-mutations";

import {
    buildChatActionsList,
    buildDetachProjectMove,
    buildDetachSceneMove,
    hasChatTarget,
    type ChatActionItem,
} from "./build-chat-actions";
import { ChatActionsDialogs } from "./chat-actions-dialogs";

export type { ChatActionId, ChatActionItem } from "./build-chat-actions";

interface UseChatActionsArgs {
    chatId: string | null;
    workspaceId: string | null;
    chatTitle: string | null;
    // Если чат закреплён — Pin превращается в Unpin. null/undefined =
    // не закреплён.
    pinnedAt?: string | null;
    // Текущая привязка чата — управляет показом пунктов «Открепить».
    projectId?: string | null;
    sceneId?: string | null;
    // Когда чат, по которому действуем, открыт сейчас. После архивации
    // или удаления активного чата редиректим на /chat (empty state).
    isActive?: boolean;
    // Разрешено ли писать (мутировать) чат: backend допускает только владельца
    // или суперадмина. Foreign-чаты, видимые менеджеру, — read-only, поэтому для
    // них действия скрываются. Fail-closed: пока флаг не известен (false),
    // любые действия прячем.
    canWrite: boolean;
}

interface UseChatActionsResult {
    actions: ChatActionItem[];
    dialogs: ReactNode;
}

// Единый источник правды для меню действий с чатом — используется и
// в ChatHeader (стрелка ↓ рядом с названием), и в sidebar-строке
// (… на ховере). Набор/порядок пунктов — в `buildChatActionsList`,
// JSX диалогов — в `ChatActionsDialogs`.
export function useChatActions({
    chatId,
    workspaceId,
    chatTitle,
    pinnedAt,
    projectId,
    sceneId,
    isActive,
    canWrite,
}: UseChatActionsArgs): UseChatActionsResult {
    const { t } = useTranslation("chat");
    const navigate = useNavigate();
    const archive = useArchiveChat();
    const pin = usePinChat();
    const unpin = useUnpinChat();
    const move = useMoveChat();
    const del = useDeleteChat();
    const [renameOpen, setRenameOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const redirectIfActive = useCallback(() => {
        if (isActive) void navigate("/chat", { replace: true });
    }, [isActive, navigate]);

    const handleArchive = useCallback(() => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        archive.mutate(target, { onSuccess: redirectIfActive });
    }, [archive, chatId, workspaceId, redirectIfActive]);

    const handlePin = useCallback(() => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        pin.mutate(target);
    }, [pin, chatId, workspaceId]);

    const handleUnpin = useCallback(() => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        unpin.mutate(target);
    }, [unpin, chatId, workspaceId]);

    const handleDetachScene = useCallback(() => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        move.mutate(buildDetachSceneMove(target, projectId));
    }, [move, chatId, workspaceId, projectId]);

    const handleDetachProject = useCallback(() => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        move.mutate(buildDetachProjectMove(target));
    }, [move, chatId, workspaceId]);

    const handleDelete = useCallback(async () => {
        const target = { chatId, workspaceId };
        if (!hasChatTarget(target)) return;
        await del.mutateAsync(target);
        setDeleteOpen(false);
        redirectIfActive();
    }, [del, chatId, workspaceId, redirectIfActive]);

    // Раскрываем оба поля по отдельности: ниже они уходят в JSX-пропы
    // `ChatActionsDialogs`, которым нужно flow-сужение самих переменных
    // (предикат по объекту сузил бы только локальный литерал, не `chatId`/`workspaceId`).
    // Fail-closed: пока клиент не знает, что он владелец/суперадмин, не показываем
    // ни одного действия. Backend в любом случае отклонит чужую запись (403),
    // так что скрытие — только защита UI от заведомо провальных мутаций.
    if (!canWrite) {
        return { actions: [], dialogs: null };
    }

    if (chatId == null || workspaceId == null) {
        return { actions: [], dialogs: null };
    }

    const actions = buildChatActionsList({
        t,
        isPinned: pinnedAt != null,
        hasScene: Boolean(sceneId),
        hasProject: Boolean(projectId),
        handlers: {
            onRename: () => setRenameOpen(true),
            onMove: () => setMoveOpen(true),
            onDetachScene: handleDetachScene,
            onDetachProject: handleDetachProject,
            onPin: handlePin,
            onUnpin: handleUnpin,
            onArchive: handleArchive,
            onDelete: () => setDeleteOpen(true),
        },
        pending: {
            pin: pin.isPending,
            unpin: unpin.isPending,
            move: move.isPending,
            archive: archive.isPending,
        },
    });

    const dialogs = (
        <ChatActionsDialogs
            chatId={chatId}
            workspaceId={workspaceId}
            chatTitle={chatTitle}
            projectId={projectId}
            sceneId={sceneId}
            renameOpen={renameOpen}
            onRenameOpenChange={setRenameOpen}
            moveOpen={moveOpen}
            onMoveOpenChange={setMoveOpen}
            deleteOpen={deleteOpen}
            onDeleteOpenChange={setDeleteOpen}
            onConfirmDelete={handleDelete}
            deletePending={del.isPending}
            t={t}
        />
    );

    return { actions, dialogs };
}
